import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { AdminSettings, Conversation, ConversationStatus, CustomerContext } from '../types';
import { relativeFromNow, formatDate, formatMoney } from '../lib/format';
import { playChime } from '../lib/alerts';
import { useStore } from '../lib/useStore';
import ChatWindow from '../components/ChatWindow';
import SetupCard from '../components/SetupCard';
import { IconChat, IconSearch, IconPin } from '../components/icons';

/**
 * Three-pane "Merchant Dashboard" layout from the design:
 * chat list (left) · active thread (center) · customer panel (right).
 * Serves both /conversations and /conversations/:id.
 */

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

const FILTERS = [
  { label: 'All', value: undefined },
  { label: 'Open', value: 'OPEN' },
  { label: 'Closed', value: 'CLOSED' },
] as const;

export default function ConversationsPage(): ReactNode {
  const navigate = useNavigate();
  const { id: selectedId } = useParams<{ id: string }>();
  const { store } = useStore();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | undefined>(undefined);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [search, setSearch] = useState('');
  const [customerContext, setCustomerContext] = useState<CustomerContext | null>(null);

  // Notification preferences (Settings page). Refs so the polling closure
  // always sees current values without re-subscribing.
  const settingsRef = useRef<AdminSettings | null>(null);
  const lastMsgIdsRef = useRef<Map<string, string> | null>(null);
  const lastReminderAtRef = useRef(0);
  const selectedIdRef = useRef<string | undefined>(selectedId);
  selectedIdRef.current = selectedId;

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then(({ settings }) => {
        if (!cancelled) settingsRef.current = settings;
      })
      .catch(() => {
        // Alerts are best-effort — without settings they simply stay off.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // New-message chime + stale-unread reminder, driven by the poll below.
  const runAlerts = useCallback((list: Conversation[]) => {
    const settings = settingsRef.current;
    const prev = lastMsgIdsRef.current;
    const next = new Map<string, string>();
    let hasNewCustomerMessage = false;
    let staleUnread = 0;

    for (const c of list) {
      const last = c.messages[c.messages.length - 1];
      if (!last) continue;
      next.set(c.id, last.id);
      const isUnread = Boolean(c.unread) && c.id !== selectedIdRef.current;
      if (!isUnread) continue;
      if (prev && prev.get(c.id) !== last.id) hasNewCustomerMessage = true;
      if (
        settings?.reminderNotifications &&
        Date.now() - new Date(last.createdAt).getTime() > settings.reminderMinutes * 60000
      ) {
        staleUnread += 1;
      }
    }
    lastMsgIdsRef.current = next;

    if (!settings) return;
    if (hasNewCustomerMessage && settings.newMessageAlerts) {
      playChime();
    }
    // Reminders repeat at most once a minute while something stays unread.
    if (staleUnread > 0 && Date.now() - lastReminderAtRef.current > 60000) {
      lastReminderAtRef.current = Date.now();
      playChime();
      window.shopify?.toast.show(
        staleUnread === 1
          ? 'A conversation is still awaiting your reply'
          : `${staleUnread} conversations are still awaiting your reply`,
      );
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = (initial: boolean) => {
      if (initial) setLoading(true);
      api
        .listConversations(statusFilter ? { status: statusFilter } : {})
        .then((r) => {
          if (cancelled) return;
          setConversations(r.conversations);
          runAlerts(r.conversations);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (err instanceof ApiError && err.status === 402) {
            setError('Upgrade to PRO to view conversations.');
          } else if (err instanceof Error) {
            setError(err.message);
          } else {
            setError('Failed to load conversations');
          }
        })
        .finally(() => {
          if (!cancelled && initial) setLoading(false);
        });
    };

    load(true);
    const interval = setInterval(() => load(false), 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [statusFilter, runAlerts]);

  // Keep the customer panel in sync with whatever ChatWindow loads/updates.
  const onConversationUpdate = useCallback((c: Conversation) => {
    setSelected(c);
  }, []);

  useEffect(() => {
    if (!selectedId) setSelected(null);
  }, [selectedId]);

  // Shopify customer context (location, order history) for the side panel.
  useEffect(() => {
    setCustomerContext(null);
    if (!selectedId) return;
    let cancelled = false;
    api
      .getCustomerContext(selectedId)
      .then((ctx) => {
        if (!cancelled) setCustomerContext(ctx);
      })
      .catch(() => {
        // Panel blocks are supplementary — fail silent, blocks stay hidden.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Client-side search across name, email, and message content.
  const visibleConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        c.customerName.toLowerCase().includes(q) ||
        c.customerEmail.toLowerCase().includes(q) ||
        c.messages.some((m) => m.content.toLowerCase().includes(q)),
    );
  }, [conversations, search]);

  const layoutClass = [
    'cg-convo-layout',
    selectedId && selected ? 'has-panel' : '',
    selectedId ? 'is-detail' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <main className="cg-content cg-content-flush" style={{ flexDirection: 'column' }}>
      {/* FREE merchants land here (Analytics is PRO) — the theme-embed
          onboarding card must still reach them. */}
      <SetupCard style={{ margin: '16px 16px 0' }} />
      <div className={layoutClass}>
        {/* Left: chat list */}
        <section className="cg-chatlist" aria-label="Conversations">
          <div className="cg-chatlist-header">
            <span className="cg-section-label">
              Active chats {loading ? '' : `(${visibleConversations.length})`}
            </span>
            <div className="cg-search">
              <IconSearch size={15} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search chats or customers…"
                aria-label="Search conversations"
              />
            </div>
            <div className="cg-filter-row">
              {FILTERS.map((f) => (
                <button
                  key={f.label}
                  type="button"
                  className={`cg-filter-chip${statusFilter === f.value ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {error ? (
            <div style={{ padding: '0 20px' }}>
              <div className="cg-banner cg-banner-warning">{error}</div>
            </div>
          ) : null}

          <div className="cg-chatlist-items">
            {loading ? (
              <div className="cg-spinner" aria-label="Loading conversations" />
            ) : visibleConversations.length === 0 && !error ? (
              <div className="cg-empty">
                <IconChat size={40} />
                <div className="cg-empty-title">
                  {search.trim() ? 'No matching chats' : 'No conversations yet'}
                </div>
                <div className="cg-empty-sub">
                  {search.trim()
                    ? 'Try a different name, email, or message keyword.'
                    : 'Customer messages from the storefront widget will appear here.'}
                </div>
              </div>
            ) : (
              visibleConversations.map((c) => {
                const lastMessage = c.messages[c.messages.length - 1];
                // Selected conversations are being read right now — don't flag.
                const unread = Boolean(c.unread) && c.id !== selectedId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={[
                      'cg-chatlist-item',
                      c.id === selectedId ? 'is-active' : '',
                      c.status === 'CLOSED' ? 'is-closed' : '',
                      unread ? 'is-unread' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => navigate(`/conversations/${c.id}`)}
                  >
                    <div className="cg-avatar">{initialsOf(c.customerName)}</div>
                    <div className="cg-chatlist-body">
                      <div className="cg-chatlist-toprow">
                        <span className="cg-chatlist-name">{c.customerName}</span>
                        <span className="cg-chatlist-time">
                          {relativeFromNow(lastMessage?.createdAt ?? c.createdAt)}
                          {unread ? <span className="cg-unread-dot" aria-label="Unread" /> : null}
                        </span>
                      </div>
                      <div className="cg-chatlist-preview">
                        {lastMessage ? `“${lastMessage.content}”` : '—'}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        {/* Center: active thread */}
        {selectedId ? (
          <ChatWindow
            conversationId={selectedId}
            plan={store?.plan ?? 'FREE'}
            onConversationUpdate={onConversationUpdate}
          />
        ) : (
          <section className="cg-thread">
            <div className="cg-empty">
              <IconChat size={40} />
              <div className="cg-empty-title">Select a conversation</div>
              <div className="cg-empty-sub">
                Pick a chat from the list to read and reply to your customer.
              </div>
            </div>
          </section>
        )}

        {/* Right: customer panel */}
        {selectedId && selected ? (
          <aside className="cg-customer-panel" aria-label="Customer details">
            <div className="cg-avatar cg-avatar-lg">{initialsOf(selected.customerName)}</div>
            <div>
              <h2 className="cg-customer-name">{selected.customerName}</h2>
              <div className="cg-customer-since">
                Customer since{' '}
                {formatDate(customerContext?.customer?.customerSince ?? selected.createdAt)}
              </div>
            </div>

            {customerContext?.customer && customerContext.customer.numberOfOrders > 1 ? (
              <span className="cg-chip cg-chip-tag">Repeat buyer</span>
            ) : null}

            <div className="cg-customer-block">
              <span className="cg-section-label">Email</span>
              <span className="cg-customer-block-value">{selected.customerEmail}</span>
            </div>

            {customerContext?.customer?.location &&
            (customerContext.customer.location.city || customerContext.customer.location.country) ? (
              <div className="cg-customer-block">
                <span className="cg-section-label">Location</span>
                <span className="cg-customer-location">
                  <IconPin size={15} />
                  <span className="cg-customer-block-value">
                    {[
                      customerContext.customer.location.city,
                      customerContext.customer.location.province,
                      customerContext.customer.location.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </span>
              </div>
            ) : null}

            {customerContext?.customer && customerContext.customer.orders.length > 0 ? (
              <div className="cg-customer-block">
                <span className="cg-section-label">
                  Order history ({customerContext.customer.numberOfOrders})
                </span>
                <div className="cg-order-list">
                  {customerContext.customer.orders.map((o) => (
                    <div key={o.name} className="cg-order-row">
                      <div>
                        <div className="cg-order-name">
                          {o.name} · {titleCase(o.fulfillmentStatus)}
                        </div>
                        <div className="cg-order-date">{formatDate(o.createdAt)}</div>
                      </div>
                      <div className="cg-order-total">
                        {formatMoney(o.totalAmount, o.currencyCode)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="cg-customer-block">
              <span className="cg-section-label">Status</span>
              <span>
                <span
                  className={`cg-chip ${selected.status === 'OPEN' ? 'cg-chip-open' : 'cg-chip-closed'}`}
                >
                  {selected.status}
                </span>
              </span>
            </div>
          </aside>
        ) : null}
      </div>
    </main>
  );
}
